// frontend/src/components/RecentCarousel.jsx
import React from "react";
import { useNavigate } from "react-router-dom";
import { apiGet, normalizeListResponse, getImageUrl } from "../lib/api";
import { trackStyleView } from "../utils/trackStyleView.js";

/**
 * RecentCarousel (responsive, gallery-sized)
 * Props:
 * - minW                 number   min card width in px (default 150)
 * - maxW                 number   max card width in px (default 210)
 * - ratio                number   height = width * ratio (default 1.2)
 * - gap                  number   px gap between cards (default 12)
 * - limit                number   max items shown (default 24)
 * - useGradientHeading   boolean  apply gradient to heading (default false)
 * - headingGradientCss   string   CSS gradient to use for the heading (optional)
 */
export default function RecentCarousel({
  minW = 150,
  maxW = 210,
  ratio = 1.2,
  gap = 12,
  limit = 24,
  useGradientHeading = false,
  headingGradientCss = "linear-gradient(135deg, #1E3A8A 0%, #6D28D9 100%)", // ← dark blue → purple
}) {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState("");
  const [canScroll, setCanScroll] = React.useState(false);
  const wrapRef = React.useRef(null);
  const navigate = useNavigate();

  // Right after the existing useEffect that loads items, add this:

// ✅ Track style views when carousel items load
React.useEffect(() => {
  if (items && items.length > 0) {
    // Get unique styles from carousel items
    const uniqueStyles = new Set();
    items.forEach(item => {
      const styleSlug = String(item.style || "").toLowerCase().trim();
      if (styleSlug && styleSlug !== "default") {
        uniqueStyles.add(styleSlug);
      }
    });
    
    // Track each unique style as "browse" context (featured content)
    uniqueStyles.forEach(styleId => {
      trackStyleView(styleId, "browse");
    });
  }
}, [items]); // Track when items change

  // Fetch data
  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        setErr("");
        let rawItems = [];
        try {
          const publicData = await apiGet(`/api/generations/home-recent?limit=${limit}`);
          const { items: pubItems } = normalizeListResponse(publicData);
          rawItems = pubItems;
        } catch (e) {
          console.log("[RecentCarousel] Public endpoint failed, trying user generations");
        }
        if (rawItems.length === 0) {
          const userData = await apiGet(`/api/generations?limit=${limit}`);
          const { items: userItems } = normalizeListResponse(userData);
          rawItems = userItems;
        }
        const normalized = rawItems
          .map((d) => ({
            id: d.id || d._id,
            src: getImageUrl(d),
            prompt: d.prompt || "",
            style: (d.style || "default").toLowerCase(),
          }))
          .filter((x) => x.id && x.src)
          .slice(0, limit);

        if (alive) setItems(normalized);
      } catch (e) {
        if (alive) setErr(e.message || "Failed to load recent");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [limit]);

  // Overflow detection
  const recalcOverflow = React.useCallback(() => {
    const el = wrapRef.current;
    if (!el) return;
    setCanScroll(el.scrollWidth > el.clientWidth + 4);
  }, []);

  React.useEffect(() => {
    recalcOverflow();
    const onResize = () => recalcOverflow();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [items, recalcOverflow]);

  // Wheel -> horizontal
  React.useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const onWheel = (e) => {
      if (Math.abs(e.deltaX) < Math.abs(e.deltaY)) {
        el.scrollLeft += e.deltaY;
        e.preventDefault();
      }
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  function scrollByCards(dir = 1) {
    const el = wrapRef.current;
    if (!el) return;
    const card = el.querySelector(".rc-card");
    const w = card ? card.getBoundingClientRect().width : maxW;
    el.scrollBy({ left: dir * w * 3, behavior: "smooth" });
  }

  function openResult(id) {
    navigate(`/result/${encodeURIComponent(id)}`);
  }

  return (
    <div
      className="rc"
      style={{
        "--rc-min-w": `${minW}px`,
        "--rc-max-w": `${maxW}px`,
        "--rc-gap": `${gap}px`,
        "--rc-ratio": ratio,
      }}
    >
      <StyleTag />

      <header className="rc-head">
        <h2
          className="h2"
          style={
            useGradientHeading
              ? {
                  background: headingGradientCss,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }
              : undefined
          }
        >
          Featured Creators
        </h2>
        {canScroll && (
          <div className="rc-arrows">
            <button className="rc-arrow" aria-label="Scroll left" onClick={() => scrollByCards(-1)}>
              ‹
            </button>
            <button className="rc-arrow" aria-label="Scroll right" onClick={() => scrollByCards(1)}>
              ›
            </button>
          </div>
        )}
      </header>

      <div className="rc-viewport" ref={wrapRef} onScroll={recalcOverflow}>
        {loading ? (
          <div className="rc-row">
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className="rc-card sk" />
            ))}
          </div>
        ) : err ? (
          <div className="rc-empty">Couldn't load recent – {err}</div>
        ) : items.length === 0 ? (
          <div className="rc-empty">No items yet. Generate and toggle "Allow on Home".</div>
        ) : (
          <div className="rc-row">
            {items.map((it) => (
              <figure
                key={it.id}
                className="rc-card"
                title={it.prompt}
                onClick={() => openResult(it.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && openResult(it.id)}
              >
                <img src={it.src} alt={it.prompt || "generation"} loading="lazy" />
                <figcaption className="rc-chip">{it.style}</figcaption>
              </figure>
            ))}
          </div>
        )}

        {canScroll && <div className="rc-fade rc-fade--left" aria-hidden />}
        {canScroll && <div className="rc-fade rc-fade--right" aria-hidden />}
      </div>
    </div>
  );
}

/* ------------------------------- Styles ---------------------------------- */
function StyleTag() {
  return (
    <style>{`
.rc { position: relative; }
.rc-head{
  display:flex; align-items:center; justify-content:space-between; margin-bottom:10px;
}
.rc-arrows{ display:flex; gap:8px; }
.rc-arrow{
  width:34px; height:34px; border-radius:10px; border:1px solid #E5E7EB; background:#fff;
  font-size:18px; font-weight:900; line-height:1; cursor:pointer;
}
.rc-arrow:hover{ background:#f7f7fb; }

/* viewport + row */
.rc-viewport{
  position:relative;
  overflow:auto;
  -webkit-overflow-scrolling: touch;
  scroll-snap-type: x mandatory;
  padding-bottom: 6px;
}
.rc-row{
  display:flex;
  gap: var(--rc-gap);
  padding:2px 2px 10px 2px;
}

/* Responsive card sizing with clamp() and ratio */
.rc-card{
  position:relative;
  width: clamp(var(--rc-min-w), 18vw, var(--rc-max-w));
  min-width: clamp(var(--rc-min-w), 18vw, var(--rc-max-w));
  height: calc(clamp(var(--rc-min-w), 18vw, var(--rc-max-w)) * var(--rc-ratio));
  border-radius:14px;
  overflow:hidden;
  border:1px solid #E5E7EB;
  background:#fff;
  box-shadow: 0 8px 18px rgba(17,24,39,.08);
  scroll-snap-align: start;
  flex: 0 0 auto;
  transition: transform .18s ease, box-shadow .18s ease;
  cursor: pointer;
}
.rc-card img{
  width:100%; height:100%; object-fit:cover; display:block;
  transform: scale(1.0);
  transition: transform .2s ease;
}
.rc-card:hover{
  transform: translateY(-4px);
  box-shadow: 0 14px 30px rgba(17,24,39,.16);
}
.rc-card:hover img{ transform: scale(1.04); }

/* chip */
.rc-chip{
  position:absolute; left:8px; bottom:8px;
  padding:4px 8px; font-size:11px; font-weight:800;
  color:#fff; background:#6D5EF9; border-radius:999px;
  box-shadow: 0 8px 18px rgba(17,24,39,.18);
}

/* skeleton */
.sk{
  background: linear-gradient(90deg,#f5f5f7,#ececf2,#f5f5f7);
  animation: rc-sk 1.2s infinite;
}
@keyframes rc-sk { 0%{background-position:-200px 0} 100%{background-position:calc(200px + 100%) 0} }

/* edge fades */
.rc-fade{
  pointer-events:none;
  position:absolute; top:0; bottom:12px; width:48px;
}
.rc-fade--left{
  left:0;
  background: linear-gradient(90deg,#fff, rgba(255,255,255,0));
}
.rc-fade--right{
  right:0;
  background: linear-gradient(270deg,#fff, rgba(255,255,255,0));
}

/* empty state */
.rc-empty{
  padding:24px;
  text-align:center;
  color:#6B7280;
  border:1px dashed #E5E7EB;
  border-radius:12px;
}

/* tighter on mobile */
@media (max-width: 560px){
  .rc-card{
    width: clamp(calc(var(--rc-min-w) - 10px), 45vw, var(--rc-max-w));
    min-width: clamp(calc(var(--rc-min-w) - 10px), 45vw, var(--rc-max-w));
    height: calc(clamp(calc(var(--rc-min-w) - 10px), 45vw, var(--rc-max-w)) * var(--rc-ratio));
  }
}
`}</style>
  );
}
